import * as c from "@/components/ui";
import InjectionDetailsFormFields from "@/features/injection/components/InjectionDetailsFormFields";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useTemplateStore } from "@/features/templates/stores/useTemplateStore";
import { services as s } from "@/services";
import { templateSettingsSchema } from "@/types/zod/quickstart-settings";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";

interface RouteParams {
	injectionDetailId: string;
	templateId: string;
}

type InjectionDetailsValues = Pick<
	z.infer<typeof templateSettingsSchema>,
	"injection_details"
>;

const InjectionDetailsFormFieldsPage = () => {
	const { templateId } = Route.useParams();
	const { injection_id, injection_details, setInjectionDetails } =
		useTemplateStore();
	const {
		markStepComplete,
		setCurrentStep,
		getNextStepRoute,
		getPreviousStepRoute,
		currentStep,
	} = useTemplateNavigationStore();
	const navigate = useNavigate();
	const form = useForm<InjectionDetailsValues>({
		resolver: zodResolver(
			templateSettingsSchema.pick({
				injection_details: true,
			}),
		),
		defaultValues: {
			injection_details: injection_details ?? [
				{
					injection_id: injection_id ?? "",
					injection_detail_id: s.uuidv4(),
					side: "",
					orthopaedic_area: "",
					site: "",
					subsite: "",
					injection_substance: "",
					dose: "",
					unit: "",
				},
			],
		},
	});

	useEffect(() => {
		setCurrentStep("injection-details");
	}, [setCurrentStep]);

	const onSubmit = async (data: InjectionDetailsValues) => {
		try {
			setInjectionDetails(data);
			markStepComplete(currentStep);
			const nextRoute = getNextStepRoute(currentStep, { templateId });
			if (nextRoute) navigate(nextRoute);
		} catch (error) {
			toast.error("Failed to save injection details");
			console.error("Form submission error:", error);
		}
	};

	const handlePrevious = () => {
		const prevRoute = getPreviousStepRoute(currentStep, { templateId });
		if (prevRoute) navigate(prevRoute);
	};

	console.log(form.formState.errors);

	return (
		<>
			<div className="mb-4">
				<h2 className="mb-2 font-semibold text-xl">
					Injection Details
				</h2>
				<p className="text-slate-500 dark:text-slate-300">
					Add details about each injection site and substance
				</p>
			</div>
			<c.ui.Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="w-full form"
				>
					<InjectionDetailsFormFields
						injectionId={injection_id ?? ""}
					/>

					{/* Navigation Buttons */}
					<div className="flex justify-between gap-4 mt-8">
						<c.ui.Button
							type="button"
							variant="outline"
							onClick={handlePrevious}
						>
							Previous
						</c.ui.Button>

						<c.ui.Button type="submit">Next</c.ui.Button>
					</div>
				</form>
			</c.ui.Form>
		</>
	);
};

export const Route = createFileRoute(
	"/_app/new/diary/$templateId/events/injection/details",
)({
	component: InjectionDetailsFormFieldsPage,
	loader: ({ params }: { params: RouteParams }) => {
		return {
			injectionDetailId: params.injectionDetailId,
		};
	},
});
